import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrdersService } from '../shared/orders.service';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ProductsService } from '../../products/shared/products.service';
import { BikeType } from '../../../models/BikeType';
import { RentalBike } from '../../../models/RentalBike';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClientsService } from '../../clients/shared/clients.service';
import { Client } from '../../../models/Client';
import { Option } from '../../../shared/data-filters/data-filter-select/data-filter-select.component';
import moment from 'moment/moment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

class NewBike {
  public quantity: number;
  public height: number;
  public bikeTypeId: number;
  public bikeType: BikeType;
  public accessories = [{
    bikeTypeId: null,
    bikeType: {
      id: null
    },
    quantity: 1
  }];
}

@Component({
    selector: 'app-update-order',
    templateUrl: './update-order.component.html',
    styleUrls: ['./update-order.component.scss'],
    standalone: false
})
export class UpdateOrderComponent implements OnInit {
  @ViewChild('bookingUpdateErrorModal', { static: true }) bookingUpdateErrorModal: TemplateRef<any>;
  @ViewChild('bookingPriceErrorModal', { static: true }) bookingPriceErrorModal: TemplateRef<any>;

  readonly EXCLUDED_BIKE_TYPES_HEIGHT_INSERTION = [12, 34]; // TANDEM, E-TANDEM

  public order: any;
  private _loadedBikes: any[] = [];
  public bikes: any[] = [];
  public orderType: string;
  public partialCancellation = false;
  public partialReturn = false;
  public currentLang: string;
  public orderFormGroup: FormGroup;

  public startDate: string;
  public endDate: string;
  public rentalPeriod: number;

  public startRentalPointName: string;
  public endRentalPointName: string;

  public newRentalBike: NewBike = new NewBike();

  public startClientOptions: Option[] = [{value: 0, label: 'All'}];
  public endClientOptions: Option[] = [{value: 0, label: 'All'}];

  public startRentalPointOptions: Option[] = [{value: null, label: 'All'}];
  public endRentalPointOptions: Option[] = [{value: null, label: 'All'}];

  public productsArray: any[];
  public bikeTypeArray: any[];
  public accessoriesTypeArray: any[];
  private _clients: Client[];
  private _contractId: string;

  public selectedStartClientId = '0';
  public selectedStartRentalPointAccountId = 0;

  public selectedEndClientId = '0';
  public selectedEndRentalPointAccountId = 0;

  constructor(private _activatedRoute: ActivatedRoute, private _ordersService: OrdersService,
    private _clientsService: ClientsService, private _translateService: TranslateService,
    private _formBuilder: FormBuilder, private _router: Router, private _modalService: NgbModal,
    private _productsService: ProductsService, private _snackBar: MatSnackBar, private _translate: TranslateService) {
    this.currentLang = this._translateService.currentLang;
    this.orderFormGroup = this._formBuilder.group({
      id: [],
      startRentalPoint: [],
      endRentalPoint: [],
      startDate: ['', [<any>Validators.required]],
      endDate: ['', [<any>Validators.required]],
      clientInformation: this._formBuilder.group({
        firstName: [],
        lastName: [],
        email: [],
        phone: [],
        address: [],
        postalCode: [],
        invoiceBike: []
      })
    });
  }

  ngOnInit() {
    this._activatedRoute.params.subscribe(params => {
      this.orderType = params['orderType'];
      this._contractId = params['contractId'];
      if (this.orderType !== 'reservation') {
        this._router.navigate(['orders/booking-reservations']);
      }
      let serviceCall1;
      switch (this.orderType) {
        case 'direct-rental':
          serviceCall1 = this._ordersService.getDirectRental(this._contractId);
          break;
        case 'booked-rental':
          serviceCall1 = this._ordersService.getBookedRentalByContractId(this._contractId);
          break;
        default:
          serviceCall1 = this._ordersService.getOnlineBookingReservationByContractId(this._contractId);
          break;
      }
      const serviceCall2 = this._productsService.getBikeTypes();
      const serviceCall3 = this._clientsService.getClients();
      combineLatest(serviceCall1, serviceCall2, serviceCall3).subscribe(result => {
        this.order = result[0];
        this.rentalPeriod = this.getDayDiff(this.order.startDate, this.order.endDate);
        this.productsArray = result[1];
        this.bikeTypeArray = this.productsArray.filter(p => p.productType === 1);

        const accessoriesInBikes = Array.from(new Set(this.bikeTypeArray
          .filter((b: any) => b.supportedAccessories?.length > 0)
          .map((b: any) => b.supportedAccessories.map((a: any) => a.bikeType))
          .flat(1)));

        this.accessoriesTypeArray = this.productsArray.filter(p => p.productType === 2 && !accessoriesInBikes.includes(p.id));

        this._clients = result[2];
        this.orderFormGroup.patchValue(this.order);
        this.orderFormGroup.controls['startRentalPoint'].setValue(this.order.startRentalPoint.accountId);
        this.orderFormGroup.controls['endRentalPoint'].setValue(this.order.endRentalPoint.accountId);
        this.startDate = this.order.startDate;
        this.endDate = this.order.endDate;
        if (this.order.status === 2) {
          this.bikes = this.order.orderReturn.returnBike;
        } else {
          switch (this.orderType) {
            case 'direct-rental':
              this.bikes = this.order.directBike;
              break;
            case 'booked-rental':
              this.bikes = this.order.bookedBike;
              break;
            default:
              this.bikes = this.order.invoiceBike;
              break;
          }
        }
        this._clients.forEach(client => {
          this.startClientOptions.push({ value: client.id, label: client.businessName});
          this.endClientOptions.push({ value: client.id, label: client.businessName});
        });

        this.startRentalPointName = result[0]['startRentalPoint']['rentalPointTranslation'][this.currentLang];
        this.endRentalPointName = result[0]['endRentalPoint']['rentalPointTranslation'][this.currentLang];
        this._loadedBikes = JSON.parse(JSON.stringify(this.bikes)); // make a copy of bikes
      });
    });
  }

  private getDayDiff(startDate: string, endDate: string): number | null {
    if (!startDate || !endDate) {
      return null;
    }
    if (typeof startDate !== 'string' || typeof endDate !== 'string') {
      return null;
    }
    const momentStartDate = moment(startDate);
    const momentendDate = moment(endDate);
    return momentendDate.diff(momentStartDate, 'days') + 1;
  }

  onOrderFormSubmit() {
    let isChanged = false;
    const rental = this.orderFormGroup.value;
    rental.startDate = this.startDate;
    rental.endDate = this.endDate;
    rental.invoiceBike = this.bikes.map((b: any) => {
      const accessories = [];
      b.accessories?.forEach((a) => {
        if (a.bikeType.id) accessories.push(a);
      });
      b.accessories = accessories;
      return {...b};
    });
    const rentalDaysDiff = this.getDayDiff(rental.startDate, rental.endDate);
    const orderDaysDiff = this.getDayDiff(this.order.startDate, this.order.endDate);

    isChanged = (
      (rentalDaysDiff !== orderDaysDiff) ||
      (this.order.startRentalPoint.accountId !== rental.startRentalPoint) ||
      (this.order.endRentalPoint.accountId !== rental.endRentalPoint) ||
      (this.order.startDate !== rental.startDate) ||
      (this.order.endDate !== rental.endDate) ||
      (this.order.clientInformation.firstName !== rental.clientInformation.firstName) ||
      (this.order.clientInformation.lastName !== rental.clientInformation.lastName) ||
      (this.order.clientInformation.email !== rental.clientInformation.email) ||
      (this.order.clientInformation.phone !== rental.clientInformation.phone) ||
      (this.order.clientInformation.address !== rental.clientInformation.address) ||
      (this.order.clientInformation.postalCode !== rental.clientInformation.postalCode) ||
      (JSON.stringify(this._loadedBikes) !== JSON.stringify(rental.invoiceBike))
    );

    rental.isChanged = (isChanged) ? 1 : 0;

    if (!rental.isChanged) {
      return this._snackBar.open(this._translate.instant('orders.updateOrder.noDataUpdate'), '', {
        duration: 3500,
      });
    }
    this._ordersService.updateReservation(rental).subscribe(result => {
        isChanged = false;
        this._snackBar.open(this._translate.instant('orders.updateOrder.reservationUpdate'), '', {
          duration: 3500,
        });
        const orderType = this.order.reservation === 0 ? 'booking' : 'reservation';
        this._router.navigate(['orders/' + orderType + '/details/' + this.order.contractId]);
      },
      (error => {
        this._modalService.open(
          error.status === 620 ? this.bookingPriceErrorModal : this.bookingUpdateErrorModal,
          { ariaLabelledBy: 'modal-basic-title', backdrop: false }
        );
      })
    );
  }

  onRemoveProduct(bikeIndex: number) {
    this.bikes.splice(bikeIndex, 1);
  }

  onRemoveBikeAccessory(bikeIndex: number) {
    this.bikes[bikeIndex].accessories = undefined;
  }

  onAddBikeClick() {
    if (!this.newRentalBike.bikeType) return;
    if (!this.newRentalBike.bikeTypeId) return;
    if (!this.newRentalBike.quantity) return;
    if (this.newRentalBike.bikeType.productType === 1) {
      if (!this.EXCLUDED_BIKE_TYPES_HEIGHT_INSERTION.includes(this.newRentalBike.bikeTypeId)) {
        if (!this.newRentalBike.height) return;
        if (isNaN(this.newRentalBike.height)) return;
      }
      if (this.newRentalBike.height < 1) return;
      if (this.newRentalBike.quantity !== 1) return;
      if (this.newRentalBike.accessories.length > 1) return;
      if (this.newRentalBike.accessories.length === 1) {
        if (this.newRentalBike.accessories[0].quantity !== 1) return;
        if (!this.newRentalBike.accessories[0].bikeTypeId) {
          this.newRentalBike.accessories = undefined;
        } else {
          this.newRentalBike.accessories[0].bikeType = this.productsArray.find(p => {
            return p.id === Number(this.newRentalBike.accessories[0].bikeTypeId);
          });
        }
      }
    }
    if (this.newRentalBike.bikeType.productType === 2) {
      if (this.newRentalBike.quantity === 0) return;
      this.newRentalBike.accessories = undefined;
    }

    this.bikes.push(RentalBike.fromDto(this.newRentalBike));
    this.newRentalBike = new NewBike();
  }

  onClientSelectChange(type: string) {
    if (type === 'startRP') {
      this.selectedStartRentalPointAccountId = 0;
      this.startRentalPointOptions = [{ value: 0, label: 'All' }];
      if (this.selectedStartClientId.toString() !== '0') {

        const selectedClient = this._clients.filter(client => client.id.toString() === this.selectedStartClientId.toString())[0];
        selectedClient.rentalPoints.forEach(rentalPoint => {
          this.startRentalPointOptions.push({ value: rentalPoint.accountId, label: rentalPoint.rentalPointTranslation[this.currentLang] });
        });
      }
    }
    if (type === 'endRP') {
      this.selectedEndRentalPointAccountId = 0;
      this.endRentalPointOptions = [{ value: 0, label: 'All' }];
      if (this.selectedEndClientId.toString() !== '0') {
        const selectedClient = this._clients.filter(client => client.id.toString() === this.selectedEndClientId.toString())[0];
        selectedClient.rentalPoints.forEach(rentalPoint => {
          this.endRentalPointOptions.push({ value: rentalPoint.accountId, label: rentalPoint.rentalPointTranslation[this.currentLang] });
        });
      }
    }

  }

  onFilterClick(type: string) {
    if (type === 'startRP') {
      this.orderFormGroup.controls['startRentalPoint'].setValue(this.selectedStartRentalPointAccountId);
      this._clients.forEach(client => {
        if (client) {
          client.rentalPoints.forEach(rentalPoint => {
            if (rentalPoint.accountId === this.selectedStartRentalPointAccountId) {
              this.startRentalPointName = rentalPoint.businessName;
            }
          });
        }
      });
    }
    if (type === 'endRP') {
      this.orderFormGroup.controls['endRentalPoint'].setValue(this.selectedEndRentalPointAccountId);
      this._clients.forEach(client => {
        if (client) {
          client.rentalPoints.forEach(rentalPoint => {
            if (rentalPoint.accountId === this.selectedEndRentalPointAccountId) {
              this.endRentalPointName = rentalPoint.businessName;
            }
          });
        }
      });
    }
  }

  onEditProductChange(productIndex: number, newBikeTypeId: number) {
    const nId = Number(newBikeTypeId);
    const newProduct = new NewBike();
    newProduct.bikeTypeId = nId;
    newProduct.bikeType = {...this.productsArray.find(p => p.id === nId)};
    if (newProduct.bikeType.productType === 2 || this.EXCLUDED_BIKE_TYPES_HEIGHT_INSERTION.includes(nId)) {
      newProduct.accessories = undefined;
    }
    newProduct.quantity = 1;
    this.bikes[productIndex] = newProduct;
  }

  onProductChange(bikeTypeId: any) {
    this.newRentalBike = new NewBike();
    this.newRentalBike.bikeTypeId = Number(bikeTypeId);
    this.newRentalBike.bikeType = this.productsArray.find(p => p.id === Number(bikeTypeId));
    if (this.newRentalBike.bikeType.productType === 1) {
      this.newRentalBike.quantity = 1;
    }
  }

}
